import { useEffect, useState } from 'react';
import { Table as TableAntd } from 'antd';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { validateFloatNumber } from '../../../../../../components/Boltz/ValidateFloatNumber';
import { transformMaskCurrency } from '../../../../../../../utils/transformMaskCurrency';
import { Container, ContentFilters } from './styles';
import { useActives } from '../../../../../../../hooks/useActives';
import { useSimulation } from '../../../../hooks/SimulationHooks';
import { ModalDesigner } from '../../../../../../../components/ModalDesigner';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#FFF',
    color: theme.palette.common.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const headerResults = [
  'Ativo',
  'Cotação',
  'Aporte',
  'Valor Aporte',
  'Qtd final',
  'Novo Patrimônio',
  'Objetivo',
  '%Simulada ',
];

interface IPropsFormAporte {
  inicialTypeAporte:
    | 'action'
    | 'fii'
    | 'all'
    | 'broker'
    | 'stocks'
    | 'etf'
    | 'reit'
    | 'crypto';
}

export function FormAporte({inicialTypeAporte}:IPropsFormAporte) {
  const {
    actions,
    all,
    fiis,
    results,
    rowSelectionAction,
    rowSelectionAll,
    rowSelectionFii,
    sendAllAssets,
    setSendAllAssets,
    setType,
    selectedTypes,
    setSelectedTypes,
    totalAport,
    type,
    updateValueTotal,
    setResults,
    typeWallet,
  } = useSimulation();
  //@ts-ignore
  const { showActive } = useActives();
  // console.log("wallet recebido:", all);
  const [showAmountActives, setShowAmountActives] = useState(false);
  const [showActiveName, setShowActiveName] = useState(false);
  const [showActiveNameResults, setShowActiveNameResults] =
    useState<boolean>(false);

  // map dos rótulos do filtro -> productTypeName do backend (singular/plural)
  const TYPE_TO_PRODUCT_TYPES: Record<string, string[]> = {
    action: ['acao'],
    fii: ['fii', 'FII'],
    stocks: ['stock', 'stocks'],
    etf: ['etf', 'etfs'],
    reit: ['reit', 'reits'],
    crypto: ['crypto', 'cripto'],
  };

  useEffect(() => {
    setSelectedTypes([
      inicialTypeAporte === 'broker' ? 'all' : inicialTypeAporte,
    ]);
  }, [inicialTypeAporte]);

  function toggleType(value: string) {
    // Todos é sempre exclusivo
    if (value === 'all') {
      setSelectedTypes(['all']);
      return;
    }

    const withoutAll = selectedTypes.filter((v) => v !== 'all');
    if (withoutAll.includes(value)) {
      const result = withoutAll.filter((v) => v !== value);
      setSelectedTypes(result.length === 0 ? ['all'] : result);
    } else {
      setSelectedTypes([...withoutAll, value]);
    }
  }

  // Sync para o context: só preserva os branches específicos action/fii
  // quando estão sozinhos (eles aplicam regras de Exception próprias).
  // Qualquer combinação ou tipo "novo" (stocks/etf/reit/crypto) cai em 'all'
  // e é filtrado localmente pelo productTypeName.
  useEffect(() => {
    if (
      selectedTypes.length === 1 &&
      (selectedTypes[0] === 'action' || selectedTypes[0] === 'fii')
    ) {
      setType(selectedTypes[0] as 'action' | 'fii');
    } else {
      setType('all');
    }
  }, [selectedTypes]);

  // const [showWarningModal, setShowWarningModal] = useState(false);

  useEffect(() => {
    setResults([]);
    // setShowWarningModal(true);
  }, []);

  const allowedProductTypes = selectedTypes.includes('all')
    ? null
    : selectedTypes.flatMap((t) => TYPE_TO_PRODUCT_TYPES[t] ?? []);

  // Não listar ativos com objetivo zerado (ValorExp == 0): não fazem parte da
  // estratégia, então não devem aparecer para seleção.
  const hasObjetivo = (it: any) => Number(it?.ValorExp) > 0;

  // No bucket "all" (etf/stock/reit/crypto/multi-seleção), em modo carteira
  // recomendada (typeWallet) um porcentagem_recomendada 0 costuma significar
  // que o motor de recomendação ainda não calcula peso pra esse tipo — não é
  // uma meta deliberada como pode ser pra uma ação/FII específica excluída da
  // carteira. Não esconder esses ativos da seleção só por causa disso.
  const hasObjetivoAll = (it: any) =>
    hasObjetivo(it) ||
    (typeWallet && Number(it?.porcentagem_recomendada) === 0);

  const filteredActions = actions.filter(hasObjetivo);
  const filteredFiis = fiis.filter(hasObjetivo);
  const filteredAll = (
    allowedProductTypes
      ? all.filter((it: any) => allowedProductTypes.includes(it.productTypeName))
      : all
  ).filter(hasObjetivoAll);

  const header = [
    {
      title: (
        <>
          Ativo{' '}
          {showActiveName ? (
            <EyeSlash
              size={20}
              color="black"
              onClick={() => setShowActiveName(!showActiveName)}
            />
          ) : (
            <Eye
              size={20}
              color="black"
              onClick={() => setShowActiveName(!showActiveName)}
            />
          )}
        </>
      ),
      dataIndex: 'tickerSymbol',
      render: (value: string) => {
        if (showActive || showActiveName) {
          return value;
        } else {
          return '*****';
        }
      },
    },
    {
      title: (
        <>
          Quantidade{' '}
          {showAmountActives ? (
            <EyeSlash
              size={20}
              color="black"
              onClick={() => setShowAmountActives(!showAmountActives)}
            />
          ) : (
            <Eye
              size={20}
              color="black"
              onClick={() => setShowAmountActives(!showAmountActives)}
            />
          )}
        </>
      ),
      dataIndex: 'equitiesQuantity',
      render: (value: number) => {
        if (showActive || showAmountActives) {
          return validateFloatNumber(value.toFixed(2).replace('.', ','));
        } else {
          return '****';
        }
      },
    },
    {
      title: 'Patrimônio',
      dataIndex: 'updateValue',
      render: (text: number | string) => {
        if (showActive) {
          return transformMaskCurrency(Number(text.toString()));
        }
        return 'R$ ******';
      },
    },
    {
      title: 'Cotação',
      dataIndex: 'closingPrice',
      render: (text: number | string) =>
        transformMaskCurrency(Number(text.toString())),
    },
    {
      title: 'Objetivo',
      dataIndex: 'ValorExp',
      render: (text: number) => `${text?.toFixed(2)}%`,
    },
    {
      title: 'Atual',
      dataIndex: 'percent',
      render: (text: number) => `${text.toFixed(2)}%`,

      defaultSortOrder: 'ascend',
      //@ts-ignore
      sorter: (a, b) => a.percent - b.percent,
    },
  ];

  // useEffect(() => {
  //   console.log('✅ results recebidos da simulação:', results);
  // }, [results]);


  return (
    <Container>
      {/* <ModalDesigner
        isOpen={showWarningModal}
        id="modal-aviso-aporte"
        title="📢 Aviso importante"
        setState={setShowWarningModal}
      >
        <p>
          O Simulador de Aportes está passando por uma manutenção para melhorias e otimizações.
        </p>
        <p>
          Em breve estará disponível novamente com uma experiência ainda melhor 🚀
        </p>
        <p>
          Agradecemos pela compreensão! 💙
        </p>
      </ModalDesigner> */}

      {results.length > 0 && (
        <>
          <br />
          <h5
            style={{
              background: '#FFF',
              width: '100%',
              padding: '1rem',
              borderRadius: '8px',
            }}
          >
            Dados da simulação
          </h5>
          <TableContainer component={Paper} sx={{ maxHeight: '300px' }}>
            <Table sx={{ minWidth: 700 }} aria-label="customized table">
              <TableHead>
                <TableRow>
                  {headerResults.map((item) => (
                    <StyledTableCell key={item}>
                      {item}{' '}
                      {item === 'Qtd final' &&
                        (showAmountActives ? (
                          <Eye
                            size={20}
                            color="black"
                            onClick={() =>
                              setShowAmountActives(!showAmountActives)
                            }
                          />
                        ) : (
                          <EyeSlash
                            size={20}
                            color="black"
                            onClick={() =>
                              setShowAmountActives(!showAmountActives)
                            }
                          />
                        ))}
                      {item === 'Ativo' &&
                        (showActiveNameResults ? (
                          <Eye
                            size={20}
                            color="black"
                            onClick={() =>
                              setShowActiveNameResults(!showActiveNameResults)
                            }
                          />
                        ) : (
                          <EyeSlash
                            size={20}
                            color="black"
                            onClick={() =>
                              setShowActiveNameResults(!showActiveNameResults)
                            }
                          />
                        ))}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {results
                  // Não listar ativos com objetivo zerado (target == 0).
                  .filter((item) => Number(item.target) > 0)
                  .filter((item) => {
                    if (sendAllAssets === 'all') return true;
                    if (sendAllAssets === 'selected') return item.percent < (item.ValorExp ?? item.target);
                    return true;
                  })
                  .map((item) => (
                    <StyledTableRow key={item.key}>
                      <StyledTableCell
                        className="reduce"
                        style={item.aportarQtde !== 0 ? {} : { color: '#adacac' }}
                      >
                        <span
                          style={
                            item.aportarQtde !== 0 ? {} : { color: '#adacac' }
                          }
                        >
                          {showActiveNameResults || showActive
                            ? item.tickerSymbol
                            : '******'}
                        </span>
                        <br />
                        <strong title={item.corporationName}>
                          {item.corporationName
                            ? showActiveNameResults || showActive
                              ? item.corporationName
                              : '******'
                            : null}
                        </strong>
                      </StyledTableCell>

                      <StyledTableCell
                        style={item.aportarQtde !== 0 ? {} : { color: '#adacac' }}
                      >
                        {transformMaskCurrency(item.closingPrice)}
                      </StyledTableCell>
                      <StyledTableCell
                        style={item.aportarQtde !== 0 ? {} : { color: '#adacac' }}
                      >
                        <b>
                          {validateFloatNumber(
                            item.aportarQtde.toFixed(2).replace('.', ',')
                          )}
                        </b>
                      </StyledTableCell>
                      <StyledTableCell
                        style={item.aportarQtde !== 0 ? {} : { color: '#adacac' }}
                      >
                        <b>{transformMaskCurrency(item.moneyAporte)}</b>
                      </StyledTableCell>
                      <StyledTableCell
                        style={item.aportarQtde !== 0 ? {} : { color: '#adacac' }}
                      >
                        {showActive || showAmountActives
                          ? validateFloatNumber(
                              item.novaQuantidade.toFixed(2).replace('.', ',')
                            )
                          : '****'}
                      </StyledTableCell>
                      <StyledTableCell
                        className="reduce"
                        title={
                          showActive
                            ? transformMaskCurrency(item.updateValue)
                            : 'R$ *******'
                        }
                        style={item.aportarQtde !== 0 ? {} : { color: '#adacac' }}
                      >
                        {showActive
                          ? transformMaskCurrency(item.updateValue)
                          : 'R$ ******'}
                      </StyledTableCell>

                      <StyledTableCell
                        style={item.aportarQtde !== 0 ? {} : { color: '#adacac' }}
                      >
                        {item.target.toFixed(2)}%
                      </StyledTableCell>
                      <StyledTableCell
                        style={
                          Number(item.target.toFixed(2)) >
                          Number(item.newpercent.toFixed(2))
                            ? { background: '#edd1d1' }
                            : { background: '#d1f0d5' }
                        }
                      >
                        {item.newpercent.toFixed(2)}%
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}

                <StyledTableRow>
                  <StyledTableCell colSpan={7}>
                    <b>Total aporte</b>
                  </StyledTableCell>
                  <StyledTableCell>
                    <b>{showActive ? totalAport : 'R$ *****'}</b>
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell colSpan={7}>
                    <b>Total</b>
                  </StyledTableCell>
                  <StyledTableCell>
                    <b>
                      {showActive
                        ? transformMaskCurrency(updateValueTotal)
                        : 'R$ *****'}
                    </b>
                  </StyledTableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <ContentFilters>
        <div className="filter">
          <label>
            <input
              type="radio"
              name="sendAllAssets"
              value="all"
              //@ts-ignore
              onChange={(e) => setSendAllAssets(e.target.value)}
              checked={sendAllAssets === 'all'}
            />
            Todos os ativos
          </label>
          <label>
            <input
              type="radio"
              name="sendAllAssets"
              value="selected"
              //@ts-ignore
              onChange={(e) => setSendAllAssets(e.target.value)}
              checked={sendAllAssets === 'selected'}
            />
            Ativos abaixo do objetivo
          </label>
        </div>

        <div className="filter">
          {[
            { value: 'all', label: 'Todos' },
            { value: 'action', label: 'Ação' },
            { value: 'fii', label: 'FIIs' },
            { value: 'stocks', label: 'Stocks' },
            { value: 'etf', label: 'ETFs' },
            { value: 'reit', label: 'REITs' },
            { value: 'crypto', label: 'Criptomoedas' },
          ].map((opt) => (
            <label key={opt.value}>
              <input
                type="checkbox"
                name="selecteType"
                value={opt.value}
                onChange={() => toggleType(opt.value)}
                checked={selectedTypes.includes(opt.value)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </ContentFilters>

      {type === 'action' ? (
        filteredActions.length !== 0 ? (
          <TableAntd
            rowSelection={{
              type: 'checkbox',
              ...rowSelectionAction,
            }}
            //@ts-ignore
            columns={header}
            dataSource={filteredActions}
            pagination={false}
            scroll={{
              x: '100vh',
              y: '300px',
            }}
          />
        ) : (
          <h1 style={{ fontSize: '1rem', margin: '1rem 0.5rem' }}>
            Ainda não há dados
          </h1>
        )
      ) : type === 'fii' ? (
        filteredFiis.length !== 0 ? (
          <TableAntd
            rowSelection={{
              type: 'checkbox',
              ...rowSelectionFii,
            }}
            //@ts-ignore
            columns={header}
            dataSource={filteredFiis}
            pagination={false}
            scroll={{
              x: '100vh',
              y: '300px',
            }}
          />
        ) : (
          <h1 style={{ fontSize: '1rem', margin: '1rem 0.5rem' }}>
            Ainda não há dados
          </h1>
        )
      ) : filteredAll.length !== 0 ? (
        <TableAntd
          rowSelection={{
            type: 'checkbox',
            ...rowSelectionAll,
          }}
          //@ts-ignore
          columns={header}
          dataSource={filteredAll}
          pagination={false}
          scroll={{
            x: '100vh',
            y: '300px',
          }}
        />
      ) : (
        <h1 style={{ fontSize: '1rem', margin: '1rem 0.5rem' }}>
          Ainda não há dados
        </h1>
      )}
    </Container>
  );
}